import type { RefObject } from "react";

import { useEffect, useState } from "react";

type Point = [number, number];

interface EchoEyesOptions {
  blinks: boolean;
  glances: boolean;
  /** `thinking`: the eyes trade heights, the two-dot loader. */
  thinks: boolean;
  followsPointer: boolean;
}

const MAX_GAZE = 1.3;
const FOLLOW_RANGE_PX = 140;
const POINTER_IDLE_MS = 2500;
const GLANCE_MS = 2400;
const THINK_MS = 450;
const BLINK_MS = 110;
const CENTRE: Point = [0, 0];
/** Per-eye lifts, alternated while thinking. */
const THINK: Point[] = [
  [0.9, -0.9],
  [-0.9, 0.9],
];
const LEVEL: Point = [0, 0];
/** Both eyes up at once: a hop. */
const HOP: Point = [1, 1];
const HOP_MS = 160;
const HOP_COOLDOWN_MS = 3000;
const HOP_CHANCE = 0.2;

/** Blinks every 4 to 6 s from a random phase; glances around (now and then
 *  hopping) when the pointer is idle, follows it when `followsPointer` and hops
 *  when it comes near, and trades eye heights while thinking. */
export const useEchoEyes = (
  ref: RefObject<Element | null>,
  { blinks, glances, thinks, followsPointer }: EchoEyesOptions,
) => {
  const [isBlinking, setIsBlinking] = useState(false);
  const [gaze, setGaze] = useState<Point>(CENTRE);
  const [lift, setLift] = useState<Point>(LEVEL);

  useEffect(() => {
    if (!thinks) return;
    let step = 0;
    const timer = window.setInterval(() => {
      step += 1;
      setLift(THINK[step % THINK.length]);
    }, THINK_MS);

    return () => {
      window.clearInterval(timer);
      setLift(LEVEL);
    };
  }, [thinks]);

  useEffect(() => {
    if (!blinks) return;
    let timer = 0;
    const schedule = (delay: number) => {
      timer = window.setTimeout(() => {
        setIsBlinking(true);
        timer = window.setTimeout(() => {
          setIsBlinking(false);
          schedule(4000 + Math.random() * 2000);
        }, BLINK_MS);
      }, delay);
    };

    schedule(Math.random() * 5000);

    return () => {
      window.clearTimeout(timer);
      setIsBlinking(false);
    };
  }, [blinks]);

  useEffect(() => {
    if (!glances) return;
    let lastMove = 0;
    let lastHop = 0;
    let isNear = false;
    let frame = 0;
    let hopTimer = 0;
    let px = 0;
    let py = 0;
    const hop = () => {
      if (Date.now() - lastHop < HOP_COOLDOWN_MS) return;
      lastHop = Date.now();
      setLift(HOP);
      hopTimer = window.setTimeout(() => setLift(LEVEL), HOP_MS);
    };
    const aim = () => {
      frame = 0;
      const box = ref.current?.getBoundingClientRect();

      if (!box) return;
      const dx = px - (box.left + box.width / 2);
      const dy = py - (box.top + box.height / 2);
      const dist = Math.hypot(dx, dy) || 1;
      const k = Math.min(1, dist / FOLLOW_RANGE_PX) * MAX_GAZE;
      const near = dist < FOLLOW_RANGE_PX;

      if (near && !isNear) hop();
      isNear = near;

      setGaze([(dx / dist) * k, (dy / dist) * k * 0.8]);
    };
    const onMove = (e: PointerEvent) => {
      px = e.clientX;
      py = e.clientY;
      lastMove = Date.now();
      if (!frame) frame = requestAnimationFrame(aim);
    };
    const glance = window.setInterval(() => {
      if (Date.now() - lastMove < POINTER_IDLE_MS) return;
      if (Math.random() < HOP_CHANCE) return hop();
      if (Math.random() >= 0.55) return setGaze(CENTRE);
      const angle = Math.random() * Math.PI * 2;
      const d = 0.6 + Math.random() * (MAX_GAZE - 0.6);

      setGaze([Math.cos(angle) * d, Math.sin(angle) * d * 0.7]);
    }, GLANCE_MS);

    if (followsPointer) document.addEventListener("pointermove", onMove);

    return () => {
      window.clearInterval(glance);
      window.clearTimeout(hopTimer);
      cancelAnimationFrame(frame);
      document.removeEventListener("pointermove", onMove);
      setGaze(CENTRE);
      setLift(LEVEL);
    };
  }, [glances, followsPointer, ref]);

  return { gaze, isBlinking, lift };
};
